import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Typography, Alert, Grid, CircularProgress, Divider, TextField as MuiTextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Formik, FieldArray } from "formik";
import FormikErrorFocus from "formik-error-focus";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { AutoAwesome, Add, RemoveCircleOutline } from "@mui/icons-material";
import PropTypes from "prop-types";

import TextField from "./TextField";
import Autocomplete from "./Autocomplete";
import DatePicker from "./DatePicker";
import SDGAutocomplete from "./SDGAutocomplete";
import ImageField from "./ImageField";
import CustomCheckbox from "./Checkbox";
import axios from "../utils/axios";
import { countWords, MAX_DESCRIPTION_WORDS } from "../utils/activityForm";
import { isBacklogDate, STUDENT_ACTIVITY_HISTORY_MONTHS } from "../utils/activityHistory";

const ELIGIBILITY_WARNING_MESSAGE =
  "This activity may not meet SVA's volunteering guidelines because it appears to involve helping an immediate family member or completing normal household responsibilities. If this activity supported the wider community, please provide more information or contact your teacher.";

// AI eligibility screening is paused in production for now (client request,
// Aug 2026) while it's refined further; stays on in staging/dev. Mirrors the
// REACT_APP_ENVIRONMENT gate already used for ChatWidget in App.js.
const AI_ELIGIBILITY_UI_ENABLED = process.env.REACT_APP_ENVIRONMENT !== "production";

/**
 * Real-time (debounced) AI screening as the student types "What did you do?"
 * and "What did you learn?" — advisory only, shown while typing so the
 * student can self-correct before submitting. The authoritative check that
 * actually decides auto-approve vs pending-review happens again server-side
 * at submission time regardless of what this preview shows. Fails open
 * (shows nothing) on any request error — never blocks typing or submission.
 */
function EligibilityCheck({ whatDidYouDo, whatDidYouLearn, onFlaggedChange }) {
  const [flagged, setFlagged] = useState(false);

  useEffect(() => {
    if (countWords(whatDidYouDo) < 3 || countWords(whatDidYouLearn) < 3) {
      setFlagged(false);
      onFlaggedChange(false);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const res = await axios.post("activities/eligibility-check", {
          what_did_you_do: whatDidYouDo,
          what_did_you_learn: whatDidYouLearn,
        });
        if (!cancelled) {
          const isFlagged = !!res.data?.data?.flagged;
          setFlagged(isFlagged);
          onFlaggedChange(isFlagged);
        }
      } catch (e) {
        // Fail open: an unavailable check should never block or alarm the student.
        if (!cancelled) {
          setFlagged(false);
          onFlaggedChange(false);
        }
      }
    }, 900);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatDidYouDo, whatDidYouLearn]);

  if (!flagged) {
    return null;
  }

  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      {ELIGIBILITY_WARNING_MESSAGE}
    </Alert>
  );
}

EligibilityCheck.propTypes = {
  whatDidYouDo: PropTypes.string,
  whatDidYouLearn: PropTypes.string,
  onFlaggedChange: PropTypes.func,
};

EligibilityCheck.defaultProps = {
  whatDidYouDo: "",
  whatDidYouLearn: "",
  onFlaggedChange: () => {},
};

const useStyles = makeStyles(() => ({
  form: {
    width: "100%",
  },
  wordCounter: {
    fontSize: 12,
    textAlign: "right",
    marginTop: 4,
  },
  aiSummaryBox: {
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: 16,
    marginTop: 8,
  },
}));

const getWordCountColor = (count, max) => {
  if (count > max) return "error";
  if (count > max * 0.8) return "warning.main";
  return "text.secondary";
};

function Form({ valuesMemo, LogActivitySchema, onSubmit, goals, activityTypes, regions, submitButtonLabel, errors, skills, sessionsLocked, beneficiaries, onLocationChange }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [eligibilityFlagged, setEligibilityFlagged] = useState(false);
  const [showEligibilityDialog, setShowEligibilityDialog] = useState(false);
  const handleFlaggedChange = useCallback((flagged) => setEligibilityFlagged(flagged), []);

  const generateAiSummary = useCallback(
    async (what_did_you_do, what_did_you_learn, selectedSkills, setFieldValue) => {
      if (!what_did_you_do || !what_did_you_learn) {
        enqueueSnackbar("Please fill in 'What did you do?' and 'What did you learn?' first", { variant: "warning" });
        return;
      }
      setGeneratingSummary(true);
      try {
        const response = await axios.post("activities/ai-summary", {
          what_did_you_do,
          what_did_you_learn,
          skills: (selectedSkills || []).map((s) => ({ id: s.id })),
        });
        const summary = response.data?.data?.ai_summary;
        if (summary) {
          setFieldValue("ai_summary", summary);
          setFieldValue("ai_summary_tagged", true);
          enqueueSnackbar("AI summary generated successfully", { variant: "success" });
        } else {
          enqueueSnackbar("Failed to generate summary. Please try again.", { variant: "error" });
        }
      } catch (error) {
        // Surface the server's specific reason (Claude busy / unavailable / contact SVA)
        // and keep it on screen long enough to read instead of a fleeting generic toast.
        const message =
          error?.response?.data?.message ||
          "Couldn't generate the AI summary right now. Please wait a moment and try again.";
        enqueueSnackbar(message, { variant: "error", autoHideDuration: 8000 });
      } finally {
        setGeneratingSummary(false);
      }
    },
    [enqueueSnackbar]
  );

  return (
    <Formik initialValues={valuesMemo} validationSchema={LogActivitySchema} onSubmit={onSubmit}>
      {(formProps) => {
        const whatDidYouDoCount = countWords(formProps.values?.what_did_you_do || "");
        const whatDidYouLearnCount = countWords(formProps.values?.what_did_you_learn || "");
        const aiSummaryCount = countWords(formProps.values?.ai_summary || "");
        const canGenerateSummary = formProps.values?.what_did_you_do && formProps.values?.what_did_you_learn;
        const hasBacklogSession = (formProps.values?.sessions || []).some((s) => isBacklogDate(s.date));

        return (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (eligibilityFlagged) {
                setShowEligibilityDialog(true);
              } else {
                formProps.handleSubmit(e);
              }
            }}
            className={classes.form}
          >
            <Grid container spacing={2}>
              {/* Row 1: Activity name & UNSDG */}
              <Grid item xs={12} sm={6}>
                <TextField required name="title" label="Activity name" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SDGAutocomplete required name="goal" options={goals} />
              </Grid>

              {/* Row 2: Activity type */}
              <Grid item xs={12} sm={6}>
                <Autocomplete name="activity_type" label="Activity type" options={activityTypes} />
              </Grid>
              <Grid item xs={12} sm={6} />

              {/* Row 3: What did you do? */}
              <Grid item xs={12}>
                <TextField
                  multiline
                  rows={3}
                  required
                  name="what_did_you_do"
                  label="What did you do?"
                  placeholder="Describe the activities you performed (max 50 words)"
                />
                <Typography
                  className={classes.wordCounter}
                  sx={{ color: getWordCountColor(whatDidYouDoCount, 50) }}
                >
                  {whatDidYouDoCount}/50 words
                </Typography>
              </Grid>

              {/* Row 4: What did you learn? */}
              <Grid item xs={12}>
                <TextField
                  multiline
                  rows={3}
                  required
                  name="what_did_you_learn"
                  label="What did you learn?"
                  placeholder="Describe what you learned from this experience (max 50 words)"
                />
                {AI_ELIGIBILITY_UI_ENABLED && (
                  <EligibilityCheck
                    whatDidYouDo={formProps.values?.what_did_you_do}
                    whatDidYouLearn={formProps.values?.what_did_you_learn}
                    onFlaggedChange={handleFlaggedChange}
                  />
                )}
                <Typography
                  className={classes.wordCounter}
                  sx={{ color: getWordCountColor(whatDidYouLearnCount, 50) }}
                >
                  {whatDidYouLearnCount}/50 words
                </Typography>
                {AI_ELIGIBILITY_UI_ENABLED && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    This activity is automatically checked to help verify it meets SVA&apos;s volunteering
                    guidelines. For your privacy, this check processes your responses in real-time without
                    storing them, and never uses them to train any AI models.
                  </Typography>
                )}
              </Grid>

              {/* Row 5: Description (editable; AI generation optional) */}
              <Grid item xs={12}>
                <Box className={classes.aiSummaryBox}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Description{" "}
                      <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: "gray" }}>
                        (optional)
                      </Typography>
                    </Typography>
                    <LoadingButton
                      variant="outlined"
                      size="small"
                      loading={generatingSummary}
                      disabled={!canGenerateSummary}
                      onClick={() =>
                        generateAiSummary(
                          formProps.values.what_did_you_do,
                          formProps.values.what_did_you_learn,
                          formProps.values.skills,
                          formProps.setFieldValue
                        )
                      }
                      startIcon={<AutoAwesome />}
                    >
                      {formProps.values.ai_summary ? "Regenerate with AI" : "Generate with AI"}
                    </LoadingButton>
                  </Box>
                  <MuiTextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Write a short description of your activity, or use the AI generator. Leave this blank to use your answers above."
                    value={formProps.values.ai_summary || ""}
                    onChange={(e) => {
                      formProps.setFieldValue("ai_summary", e.target.value);
                      formProps.setFieldValue("ai_summary_tagged", false);
                    }}
                    onBlur={() => formProps.setFieldTouched("ai_summary", true)}
                    error={(formProps.touched.ai_summary || formProps.submitCount > 0) && !!formProps.errors.ai_summary}
                    helperText={((formProps.touched.ai_summary || formProps.submitCount > 0) && formProps.errors.ai_summary) || " "}
                  />
                  <Typography
                    className={classes.wordCounter}
                    sx={{ color: getWordCountColor(aiSummaryCount, MAX_DESCRIPTION_WORDS) }}
                  >
                    {aiSummaryCount}/{MAX_DESCRIPTION_WORDS} words
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    For your privacy, this AI feature processes your requests in real-time without storing your data, and your inputs are never used to train any AI models.
                  </Typography>
                  {!canGenerateSummary && (
                    <Typography variant="caption" color="text.secondary">
                      Fill in &quot;What did you do?&quot; and &quot;What did you learn?&quot; above to use the AI generator.
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Row 6: Region, Address & Beneficiary */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  required
                  name="location"
                  label="Region"
                  options={regions}
                  onChange={(val) => onLocationChange && onLocationChange(val)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="address" label="Address" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  name="beneficiary"
                  label={formProps.values.location ? "Beneficiary" : "Beneficiary (select a region first)"}
                  options={beneficiaries || []}
                  disabled={!formProps.values.location}
                  noOptionsText={beneficiaries?.length === 0 ? "Select a region first" : "No beneficiaries found"}
                />
              </Grid>

              {/* Row 7: Sessions */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Sessions
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {sessionsLocked && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This activity has been verified. Sessions cannot be added or removed.
                  </Alert>
                )}
                <FieldArray name="sessions">
                  {({ push, remove, form }) => (
                    <Box>
                      {(form.values.sessions || []).map((session, index) => (
                        <Box key={index} display="flex" gap={2} sx={{ mb: 2 }}>
                          <Box flex={1}>
                            <DatePicker required disabled={sessionsLocked} name={`sessions[${index}].date`} label="Start date" />
                          </Box>
                          <Box flex={1} sx={{ position: "relative" }}>
                            {index > 0 && !sessionsLocked && (
                              <Box
                                display="flex"
                                alignItems="center"
                                sx={{ position: "absolute", right: 0, top: 0, cursor: "pointer" }}
                                onClick={() => remove(index)}
                              >
                                <RemoveCircleOutline sx={{ fontSize: 12, color: "error.main", mr: 0.5 }} />
                                <Typography sx={{ fontSize: 12, color: "error.main" }}>
                                  Remove session
                                </Typography>
                              </Box>
                            )}
                            <TextField
                              required
                              disabled={sessionsLocked}
                              name={`sessions[${index}].hours`}
                              label="No. of hours"
                              type="number"
                              inputProps={{ min: 0.5, max: 8, step: 0.5 }}
                            />
                          </Box>
                        </Box>
                      ))}
                      {!sessionsLocked && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Add />}
                          onClick={() => push({ date: null, hours: "" })}
                        >
                          Add Session
                        </Button>
                      )}
                    </Box>
                  )}
                </FieldArray>
                {typeof formProps.errors.sessions === "string" && formProps.submitCount > 0 && (
                  <Alert severity="error" sx={{ mt: 1 }}>{formProps.errors.sessions}</Alert>
                )}
                <Divider sx={{ mt: 2 }} />
              </Grid>

              {/* Backlogged activity: warning + optional supporting evidence */}
              {hasBacklogSession && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This activity is more than {STUDENT_ACTIVITY_HISTORY_MONTHS} months old and will be sent to
                    your teacher or SVA for review before the hours count toward your total.
                  </Alert>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Supporting evidence{" "}
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: "gray" }}>
                      (optional, helps your reviewer verify this activity)
                    </Typography>
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <ImageField name="evidence_photo" label="Supporting photo" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ImageField name="evidence_certificate" label="Certificate" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField name="reference_contact_name" label="Reference contact name" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField name="reference_contact_email" label="Reference contact email" />
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Row 8: Skills */}
              <Grid item xs={12}>
                <Autocomplete name="skills" multiple label="Skills" options={skills} />
              </Grid>
            </Grid>

            {AI_ELIGIBILITY_UI_ENABLED && (
              <Box mt={2}>
                <CustomCheckbox
                  required
                  name="eligibility_confirmed"
                  label="I confirm this activity was unpaid, benefited the community or someone outside my family, and meets SVA's volunteering guidelines."
                />
              </Box>
            )}

            <Box my={3}>
              <Button
                type="submit"
                variant="contained"
                disabled={formProps.isSubmitting || (AI_ELIGIBILITY_UI_ENABLED && !formProps.values.eligibility_confirmed)}
              >
                {submitButtonLabel}
              </Button>
              {formProps.submitCount > 0 && Object.keys(formProps.errors).length > 0 && (
                <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                  Please complete the highlighted fields above.
                </Typography>
              )}
            </Box>

            {errors.map((text) => (
              <Alert key={text} severity="error" sx={{ mb: 1 }}>
                {text}
              </Alert>
            ))}
            <FormikErrorFocus offset={0} align="top" focusDelay={200} ease="linear" duration={1000} />

            <Dialog open={showEligibilityDialog} onClose={() => setShowEligibilityDialog(false)}>
              <DialogTitle>This may not qualify as volunteering</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  This activity may not qualify as volunteering and will be submitted to the SVA team for review.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowEligibilityDialog(false);
                    formProps.submitForm();
                  }}
                >
                  OK, continue
                </Button>
              </DialogActions>
            </Dialog>
          </form>
        );
      }}
    </Formik>
  );
}

export default function IndividualActivityForm({
  initialValues = {},
  LogActivitySchema,
  handleSubmit,
  submitButtonLabel = "LOG ACTIVITY",
  sessionsLocked = false,
}) {
  const { locations: regions, skills, activity_types: activityTypes, goals } = useSelector(state => state.data);

  const [errors, setErrors] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);

  const handleLocationChange = useCallback(async (location) => {
    if (location?.id) {
      try {
        const res = await axios.get(`beneficiaries?filter[location_id]=${location.id}`);
        setBeneficiaries(res.data?.data || []);
      } catch (e) {
        setBeneficiaries([]);
      }
    } else {
      setBeneficiaries([]);
    }
  }, []);

  const onSubmit = useCallback(
    async (values, { setSubmitting }) => {
      try {
        setErrors([]);
        await handleSubmit(values);
        setSubmitting(false);
      } catch (error) {
        const apiErrors = error?.response?.data?.errors;
        setErrors(apiErrors ? Object.values(apiErrors).flat() : [error?.response?.data?.message || error?.message || "Something went wrong. Please try again."]);
        setSubmitting(false);
      }
    },
    [handleSubmit]
  );

  const valuesMemo = useMemo(() => {
    const values = { ...initialValues };
    if (!values.sessions) {
      values.sessions = [{ date: null, hours: "" }];
    }
    if (values.eligibility_confirmed === undefined || !AI_ELIGIBILITY_UI_ENABLED) {
      values.eligibility_confirmed = !AI_ELIGIBILITY_UI_ENABLED;
    }
    if (values.goal_id) {
      values.goal = goals.find((goal) => goal.id === values.goal_id);
    }
    if (values.location_id) {
      values.location = regions.find((location) => location.id === values.location_id);
    }
    if (values.activity_type_id) {
      values.activity_type = activityTypes.find((activityType) => activityType.id === values.activity_type_id);
    }
    // Pre-load beneficiaries when editing an activity with a location
    if (values.location_id && !beneficiaries.length) {
      handleLocationChange({ id: values.location_id });
    }
    return values;
  }, [regions, goals, activityTypes, initialValues, beneficiaries.length, handleLocationChange]);

  if (!regions.length || !goals.length || !activityTypes.length || !skills.length) {
    return (
      <Box my={20}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Form
      valuesMemo={valuesMemo}
      LogActivitySchema={LogActivitySchema}
      onSubmit={onSubmit}
      goals={goals}
      skills={skills}
      activityTypes={activityTypes}
      regions={regions}
      submitButtonLabel={submitButtonLabel}
      errors={errors}
      sessionsLocked={sessionsLocked}
      beneficiaries={beneficiaries}
      onLocationChange={handleLocationChange}
    />
  );
}

IndividualActivityForm.defaultProps = {
  initialValues: {},
  handleSubmit: () => {},
  submitButtonLabel: "LOG ACTIVITY",
};

IndividualActivityForm.propTypes = {
  initialValues: PropTypes.shape({}),
  LogActivitySchema: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  submitButtonLabel: PropTypes.string,
  sessionsLocked: PropTypes.bool,
};

Form.propTypes = {
  valuesMemo: PropTypes.shape({
    title: PropTypes.string,
    what_did_you_do: PropTypes.string,
    what_did_you_learn: PropTypes.string,
    ai_summary: PropTypes.string,
    sessions: PropTypes.arrayOf(PropTypes.shape({
      date: PropTypes.any,
      hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
  }).isRequired,
  LogActivitySchema: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  goals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ).isRequired,
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ).isRequired,
  activityTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ).isRequired,
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ).isRequired,
  submitButtonLabel: PropTypes.string.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  sessionsLocked: PropTypes.bool,
};
